/**
 * Player settings, in localStorage.
 *
 * Nothing here is a secret, and nothing here is sent to the server — these are
 * preferences about how this device behaves. The seat token, which IS a
 * credential, lives separately in `net/connection.ts` so that a "reset my
 * settings" action can never be the thing that logs somebody out of a saloon
 * mid-wave.
 */

import type { AimMode } from '../hunt/aim.js';

export interface Settings {
  /** Which input mode the player has chosen. All three are peers. */
  mode: AimMode;
  /** 0.5 … 2.0. Ignored by mouse aiming, which is 1:1 with the screen. */
  sensitivity: number;
  invertY: boolean;
  sound: boolean;
  music: boolean;
  /** 0 … 1. */
  volume: number;
  /** Show the FIRE button even when the whole screen is a trigger. */
  fireButton: boolean;
  /** Has the player been through the range once? */
  trained: boolean;
}

const KEY = 'saloon.settings.v2';
const OLD_KEY = 'saloon.settings.v1';

/**
 * Does this machine have a mouse or a trackpad?
 *
 * `(pointer: fine)` asks about the PRIMARY pointer specifically, which is the
 * question being asked here: a phone with a stylus paired still wants the
 * light-gun default, and a laptop with a touchscreen still wants the mouse.
 * Everything this decides is a default the player can override.
 */
export function hasFinePointer(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(pointer: fine)').matches;
}

export function defaultMode(): AimMode {
  return hasFinePointer() ? 'mouse' : 'gyro';
}

/** How a mode is named in a sentence. */
export function modeWord(mode: AimMode): string {
  return mode === 'gyro' ? 'the phone' : mode === 'mouse' ? 'the mouse' : 'thumb aiming';
}

function clampNum(v: unknown, lo: number, hi: number, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
}

function coerceMode(v: unknown): AimMode | null {
  return v === 'gyro' || v === 'touch' || v === 'mouse' ? v : null;
}

export function loadSettings(): Settings {
  const fallback: Settings = {
    mode: defaultMode(),
    sensitivity: 1,
    invertY: false,
    sound: true,
    music: true,
    volume: 0.85,
    fireButton: true,
    trained: false,
  };

  try {
    /**
     * The v1 → v2 migration exists because v1 had no mouse mode, so every
     * desktop player who had ever anted up has `touch` written to disk — the
     * mode the app moved them to when it found no motion sensor. Reading that
     * back verbatim would hand exactly the players this release is for the
     * worst of the three inputs, silently. So a v1 record keeps its
     * sensitivity, sound and training flags and gets a fresh mode.
     */
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      const old = localStorage.getItem(OLD_KEY);
      if (!old) return fallback;
      const p = JSON.parse(old) as Partial<Settings>;
      return {
        ...fallback,
        sensitivity: clampNum(p.sensitivity, 0.5, 2, 1),
        invertY: p.invertY === true,
        sound: p.sound !== false,
        music: p.music !== false,
        fireButton: p.fireButton !== false,
        trained: p.trained === true,
      };
    }

    const p = JSON.parse(raw) as Partial<Settings>;
    return {
      mode: coerceMode(p.mode) ?? fallback.mode,
      sensitivity: clampNum(p.sensitivity, 0.5, 2, 1),
      invertY: p.invertY === true,
      sound: p.sound !== false,
      music: p.music !== false,
      volume: clampNum(p.volume, 0, 1, 0.85),
      fireButton: p.fireButton !== false,
      trained: p.trained === true,
    };
  } catch {
    return fallback;
  }
}

export function saveSettings(s: Settings) {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* Safari private mode. The session still works, it just forgets. */
  }
}

export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
